import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { dateOnly, money, statusClass, statusLabel } from '../lib/format.ts';

interface SupplierDetail {
  id: number;
  creditorNo: string;
  name: string;
  currency: string;
  country: string | null;
  balance: number;
  balanceStr: string | null;
  termText: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  recentPurchases: Array<{
    purchaseNo: string;
    purchaseDate: string;
    value: number;
    status: string;
    sourcePoNo: string | null;
  }>;
  recentPurchaseOrders: Array<{
    poNo: string;
    poDate: string;
    value: number;
    status: string;
  }>;
}

export function SupplierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['supplier', id],
    queryFn: () =>
      apiFetch<SupplierDetail>(`/api/dashboard/suppliers/${encodeURIComponent(id ?? '')}`),
    enabled: !!id,
  });

  return (
    <>
      <Link to="/suppliers" className="back-link">← All suppliers</Link>

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <>
          <header className="main-header">
            <div>
              <h1 className="main-title">{data.name}</h1>
              <span className="main-subtitle mono">{data.creditorNo}</span>
            </div>
          </header>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Master record</h2>
            </header>
            <dl className="detail-grid">
              <dt>Creditor code</dt>
              <dd className="mono">{data.creditorNo}</dd>
              <dt>Currency</dt>
              <dd>{data.currency}</dd>
              <dt>Country</dt>
              <dd>{data.country || '—'}</dd>
              <dt>Balance</dt>
              <dd>{data.balanceStr || money(data.balance, data.currency)}</dd>
              <dt>Payment terms</dt>
              <dd>{data.termText || <em style={{ color: 'var(--text-dim)' }}>(detail-fetch pass pending)</em>}</dd>
              <dt>Phone</dt>
              <dd className="mono">{data.contactPhone || '—'}</dd>
              <dt>Email</dt>
              <dd>{data.contactEmail || '—'}</dd>
            </dl>
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Recent purchases</h2>
              <span className="panel-subtitle">{data.recentPurchases.length} most recent</span>
            </header>
            {data.recentPurchases.length === 0 ? (
              <div className="empty">No purchases on file.</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Purchase no</th>
                    <th>Date</th>
                    <th>Source PO</th>
                    <th className="num">Value</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentPurchases.map((p) => (
                    <tr key={p.purchaseNo}>
                      <td className="mono">{p.purchaseNo}</td>
                      <td className="mono">{dateOnly(p.purchaseDate)}</td>
                      <td className="mono">{p.sourcePoNo || '—'}</td>
                      <td className="num">{money(p.value, data.currency)}</td>
                      <td>
                        <span className={`badge ${statusClass(p.status)}`}>
                          {statusLabel(p.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Recent purchase orders</h2>
              <span className="panel-subtitle">{data.recentPurchaseOrders.length} most recent</span>
            </header>
            {data.recentPurchaseOrders.length === 0 ? (
              <div className="empty">No purchase orders on file.</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>PO no</th>
                    <th>Date</th>
                    <th className="num">Value</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentPurchaseOrders.map((p) => (
                    <tr key={p.poNo}>
                      <td className="mono">{p.poNo}</td>
                      <td className="mono">{dateOnly(p.poDate)}</td>
                      <td className="num">{money(p.value, data.currency)}</td>
                      <td>
                        <span className={`badge ${statusClass(p.status)}`}>
                          {statusLabel(p.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </>
  );
}
